import Phaser from 'phaser';

export class SfxManager {
  constructor(scene, manifest = {}) {
    this.scene = scene;
    this.manifest = manifest || {};
    this.cooldowns = new Map(); // `${playerId}:${category}` -> ms remaining
    this.globalMaxVoices = 24;
    this.baseVolume = 0.3; // decreased by 50% (was 0.6)
    this.playerId = p => p.walletAddress || p.name || 'unknown';
    this.zoomMin = scene.initialWorldZoomMin || 0.1;
    this.zoomMax = 1.5;
  }

  update(delta) {
    if (!this.cooldowns.size) return;
    for (const [k, v] of this.cooldowns) {
      const nv = Math.max(0, v - delta);
      if (nv <= 0) this.cooldowns.delete(k);
      else this.cooldowns.set(k, nv);
    }
  }

  registerPlayer(player) {
    // Size influences pitch subtly; add persona jitter ±5%; increased by 20%
    const scale = player.sprite?.scale || 0.12;
    const sizePitch = Math.pow(scale / 0.12, 0.25);
    const jitter = 1 + Phaser.Math.FloatBetween(-0.3, 0.3);
    player.sfxPitchBase = sizePitch * jitter * 1.5; // increased by 20%
  }

  setZoomBounds(minZoom, maxZoom) {
    this.zoomMin = Math.max(0.001, minZoom);
    this.zoomMax = Math.max(this.zoomMin + 0.001, maxZoom);
  }

  getCameraVolumeScale() {
    const z = this.scene.worldCamera?.zoom || this.zoomMin;
    const t = Phaser.Math.Clamp((z - this.zoomMin) / (this.zoomMax - this.zoomMin), 0, 1);
    return 0.35 + 0.65 * t; // fade in with zoom
  }

  getCrowdScale() {
    const alive = (this.scene.players || []).filter(p => p.isAlive).length || 1;
    return Phaser.Math.Clamp(10 / Math.sqrt(Math.max(10, alive)), 0.15, 1); // dim if huge crowd
  }

  getPlayProbability() {
    const alive = (this.scene.players || []).filter(p => p.isAlive).length || 1;
    if (alive <= 10) return 1.0;
    if (alive >= 100) return 0.1;
    // Linear interpolation: 1.0 at 20, 0.1 at 100
    return 1.0 - (0.9 * (alive - 20) / (100 - 20));
  }

  canPlay(category, player, cooldownMs = 200) {
    // Global voice cap
    const active = (this.scene.sound?.sounds || []).filter(s => s.isPlaying).length;
    if (active >= this.globalMaxVoices) return false;
    const id = this.playerId(player);
    const key = `${id}:${category}`;
    const cd = this.cooldowns.get(key) || 0;
    if (cd > 0) return false;
    this.cooldowns.set(key, cooldownMs);
    return true;
  }

  async playCategory(category, player, { volumeMul = 1.0, detune = null, rate = null } = {}) {
    const list = this.manifest[category] || [];
    if (!list.length || !this.scene.sound) return;

    // Get a random sound entry (can be string or object with {id, file, default})
    const entry = Phaser.Utils.Array.GetRandom(list);
    
    // Use the same key format as JoinScene: sfx_${category}_${sound.id}
    let key;
    if (typeof entry === 'object' && entry) {
      key = `sfx_${category}_${entry.id || entry.file.replace(/[^a-zA-Z0-9]/g, '_')}`;
    } else {
      key = `sfx_${category}_${String(entry).replace(/[^a-zA-Z0-9]/g, '_')}`;
    }
    
    const isDefault = (typeof entry === 'object' && entry) ? entry.default === true : false;
    
    const vol = this.baseVolume * this.getCameraVolumeScale() * this.getCrowdScale() * volumeMul;
    const rateFinal = (rate ?? 1) * (player?.sfxPitchBase || 1);
    const tryPlay = () => {
      const s = this.scene.sound.get(key) || this.scene.sound.add(key);
      if (!s) return;
      if (detune == null) s.setRate(rateFinal);
      else s.setDetune(detune);
      s.setVolume(Phaser.Math.Clamp(vol, 0, 1));
      s.play();
    };

    if (this.scene.cache.audio?.exists(key)) {
      tryPlay();
    } else {
      // If sound not found in cache, it means it wasn't preloaded properly
      // This shouldn't happen if JoinScene preloading worked correctly
      console.warn(`⚠️ SFX not found in cache: ${key}. This should have been preloaded in JoinScene.`);
      
      // Fallback: try to load using the original file path
      this.scene.load.once('complete', tryPlay);
      try {
        const theme = this.scene.getCurrentTheme?.() || {};
        const awsRegion = process.env.REACT_APP_AWS_REGION || 'us-east-1';
        
        let s3BasePath;
        if (isDefault) {
          // Load from default theme path
          s3BasePath = `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/soulwars-default`;
        } else {
          // Load from current theme path
          s3BasePath = theme?.s3BasePath || (theme?.id ? `https://soulwars.s3.${awsRegion}.amazonaws.com/themes/${theme.id}` : '');
        }
        
        // Use the original file path for loading, but keep the JoinScene key format
        const originalFilePath = (typeof entry === 'object' && entry) ? entry.file : entry;
        const url = s3BasePath ? `${s3BasePath}/${originalFilePath}` : `assets/${originalFilePath}`;
        this.scene.load.audio(key, url);
      } catch (_) {
        const originalFilePath = (typeof entry === 'object' && entry) ? entry.file : entry;
        this.scene.load.audio(key, `assets/${originalFilePath}`);
      }
      this.scene.load.start();
    }
  }

  // Events
  onAttack(player) {
    if (!this.canPlay('attack_grunt', player, 450) || Phaser.Math.FloatBetween(0, 1) > this.getPlayProbability()) return;
    this.playCategory('attack_grunt', player, { rate: Phaser.Math.FloatBetween(0.95, 1.05) });

    // Play weapon whoosh sound
    if (Phaser.Math.FloatBetween(0, 1) <= this.getPlayProbability() * 0.7) { // 70% of normal rate
      this.playCategory('melee_whoosh', player, {
        rate: Phaser.Math.FloatBetween(0.9, 1.1),
        volumeMul: 0.6
      });
    }
  }

  onHit(victim, damage, willDie = false) {
    const severe = damage >= 15;
    const cat = severe ? 'scream_big' : 'pain';
    if (!this.canPlay(cat, victim, severe ? 1200 : 500) || Phaser.Math.FloatBetween(0, 1) > this.getPlayProbability()) return;
    this.playCategory(cat, victim, {
      rate: Phaser.Math.FloatBetween(0.95, 1.05),
      volumeMul: severe ? 1.2 : 1.0
    });

    // Play weapon impact sound - gore if death blow, regular hit otherwise
    const weaponCat = willDie ? 'melee_gore' : 'melee_hit';
    if (Phaser.Math.FloatBetween(0, 1) <= this.getPlayProbability() * 0.8) { // 80% of normal rate
      this.playCategory(weaponCat, victim, {
        rate: Phaser.Math.FloatBetween(0.95, 1.05),
        volumeMul: willDie ? 0.9 : 0.7
      });
    }

    // Revenge scream chance if badly hurt but alive
    /*if (victim.isAlive && victim.health / victim.maxHealth < 0.3 && Phaser.Math.Between(0, 100) < 15) {
      if (this.canPlay('scream_mad', victim, 2000) && Phaser.Math.FloatBetween(0, 1) <= this.getPlayProbability()) {
        this.playCategory('scream_mad', victim, { rate: Phaser.Math.FloatBetween(0.95, 1.05) });
      }
    }*/
  }

  onBreath(player) {
    if (!this.canPlay('breath_tired', player, 2000) || Phaser.Math.FloatBetween(0, 1) > this.getPlayProbability()) return;
    this.playCategory('breath_tired', player, { rate: Phaser.Math.FloatBetween(0.95, 1.05), volumeMul: 0.8 });
  }

  onDeath(player) {
    if (!this.canPlay('death', player, 3000) || Phaser.Math.FloatBetween(0, 1) > this.getPlayProbability()) return;
    this.playCategory('death', player, { rate: Phaser.Math.FloatBetween(0.95, 1.05) });
  }
}
